import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Search, 
  Plus, 
  Edit,
  Trash2,
  GraduationCap,
  CheckCircle,
  AlertTriangle,
  Clock,
  Info,
  Settings,
  List,
  FileDown,
  Archive
} from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

export default function GestionFormations() {
  const { typesFormations, refreshTypesFormations } = useApp();
  const [activeTab, setActiveTab] = useState('formations');
  
  // Formations state
  const [formations, setFormations] = useState([]);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showArchives, setShowArchives] = useState(false);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [selectedTypeFormation, setSelectedTypeFormation] = useState(null);
  const [formData, setFormData] = useState({
    collaborateur_id: '',
    type_formation_id: '',
    date_formation: '',
    date_fin: '',
    organisme: '',
    numero_attestation: ''
  });

  // Types Formations state
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [deleteTypeDialogOpen, setDeleteTypeDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({
    code: '',
    intitule: '',
    description: '',
    validite_type: 'duree',
    validite_mois: 24
  });

  useEffect(() => {
    loadData();
  }, [showArchives]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [formationsData, collabsData] = await Promise.all([
        api.getAllFormations(showArchives),
        api.getCollaborateurs()
      ]);
      setFormations(formationsData);
      setCollaborateurs(collabsData);
    } catch (error) {
      toast.error('Erreur lors du chargement des formations');
    } finally {
      setLoading(false);
    }
  };

  // ==================== FORMATIONS HANDLERS ====================
  
  const handleTypeChange = (typeId) => {
    setFormData({ ...formData, type_formation_id: typeId });
    const type = typesFormations.find(t => t.id === typeId);
    setSelectedTypeFormation(type);
    
    if (type && formData.date_formation && type.validite_type === 'duree' && type.validite_mois) {
      const dateForm = new Date(formData.date_formation);
      dateForm.setMonth(dateForm.getMonth() + type.validite_mois);
      setFormData(prev => ({ ...prev, type_formation_id: typeId, date_fin: dateForm.toISOString().split('T')[0] }));
    }
  };

  const handleDateFormationChange = (date) => {
    setFormData({ ...formData, date_formation: date });
    
    if (selectedTypeFormation && selectedTypeFormation.validite_type === 'duree' && selectedTypeFormation.validite_mois) {
      const dateForm = new Date(date);
      dateForm.setMonth(dateForm.getMonth() + selectedTypeFormation.validite_mois);
      setFormData(prev => ({ ...prev, date_formation: date, date_fin: dateForm.toISOString().split('T')[0] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let formationId;
      
      if (selectedFormation) {
        await api.updateFormation(selectedFormation.id, formData);
        formationId = selectedFormation.id;
        toast.success('Formation mise à jour');
      } else {
        const result = await api.createFormation(formData);
        formationId = result.id;
        toast.success('Formation ajoutée');
      }
      
      // Upload du justificatif si fourni
      if (formData.document && formationId) {
        try {
          await api.uploadDocument(formData.document, 'formation', formationId);
          toast.success('Justificatif joint avec succès');
        } catch (uploadError) {
          console.error('Error uploading document:', uploadError);
          toast.warning('Formation enregistrée mais erreur lors de l\'upload du justificatif');
        }
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (!selectedFormation) return;
    try {
      await api.deleteFormation(selectedFormation.id);
      toast.success('Formation supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedFormation(null);
    }
  };

  const handleDownloadAttestation = async (formation) => {
    try {
      toast.info('Génération de l\'attestation en cours...');
      const result = await api.generateFormationAttestation(formation.id);
      
      if (result.html) {
        // Extract styles from the original HTML
        const styleMatch = result.html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
        const originalStyles = styleMatch ? styleMatch[0] : '';
        
        // Extract body content from the original HTML
        const bodyMatch = result.html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        const bodyContent = bodyMatch ? bodyMatch[1] : result.html.replace(/<html>|<\/html>|<head>.*?<\/head>|<!DOCTYPE html>/gis, '');
        
        // Create a Blob with the HTML content including original styles
        const htmlWithPrintStyles = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <title>Attestation de Formation - ${formation.collaborateur_nom || ''}</title>
            ${originalStyles}
            <style>
              @media print {
                body { margin: 0; }
                .no-print { display: none !important; }
              }
            </style>
          </head>
          <body>
            <div class="no-print" style="position: fixed; top: 10px; right: 10px; z-index: 9999;">
              <button onclick="window.print()" style="padding: 12px 24px; background: #059669; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: bold;">
                🖨️ Imprimer / Enregistrer en PDF
              </button>
              <p style="font-size: 12px; color: #666; margin-top: 8px;">Astuce: Choisissez "Enregistrer en PDF" comme imprimante</p>
            </div>
            ${bodyContent}
          </body>
          </html>
        `;
        
        // Create blob and open in new tab
        const blob = new Blob([htmlWithPrintStyles], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        toast.success('Attestation ouverte - Utilisez le bouton pour enregistrer en PDF');
      } else {
        throw new Error('Aucun contenu généré');
      }
    } catch (error) {
      console.error('Error generating attestation:', error);
      toast.error('Erreur lors de la génération de l\'attestation: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const resetForm = () => {
    setSelectedFormation(null);
    setSelectedTypeFormation(null);
    setFormData({
      collaborateur_id: '',
      type_formation_id: '',
      date_formation: '',
      date_fin: '',
      organisme: '',
      numero_attestation: ''
    });
  };

  // ==================== TYPES FORMATIONS HANDLERS ====================

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedType) {
        await api.updateTypeFormation(selectedType.id, typeFormData);
        toast.success('Type de formation mis à jour');
      } else {
        await api.createTypeFormation(typeFormData);
        toast.success('Type de formation créé');
      }
      setTypeDialogOpen(false);
      resetTypeForm();
      refreshTypesFormations();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteType = async () => {
    if (!selectedType) return;
    try {
      await api.deleteTypeFormation(selectedType.id);
      toast.success('Type de formation supprimé');
      refreshTypesFormations();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la suppression');
    } finally {
      setDeleteTypeDialogOpen(false);
      setSelectedType(null);
    }
  };

  const openEditType = (type) => {
    setSelectedType(type);
    setTypeFormData({
      code: type.code,
      intitule: type.intitule,
      description: type.description || '',
      validite_type: type.validite_type,
      validite_mois: type.validite_mois || 24
    });
    setTypeDialogOpen(true);
  };

  const resetTypeForm = () => {
    setSelectedType(null);
    setTypeFormData({
      code: '',
      intitule: '',
      description: '',
      validite_type: 'duree',
      validite_mois: 24
    });
  };

  // ==================== FILTERS ====================

  const getStatut = (formation) => {
    if (formation.validite_type === 'vie') return 'valide';
    if (!formation.date_fin) return 'valide';
    const today = new Date();
    const dateFin = new Date(formation.date_fin);
    const daysUntil = Math.ceil((dateFin - today) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return 'expire';
    if (daysUntil <= 30) return 'expire_soon';
    return 'valide';
  };

  const filteredFormations = formations.filter(f => {
    const matchSearch = search === '' || 
      f.intitule?.toLowerCase().includes(search.toLowerCase()) ||
      f.collaborateur_nom?.toLowerCase().includes(search.toLowerCase());
    const matchStatut = statutFilter === 'all' || getStatut(f) === statutFilter;
    const matchType = typeFilter === 'all' || f.type_formation_id === typeFilter;
    return matchSearch && matchStatut && matchType;
  });

  // Statistics - only count non-archived items for active stats
  const activeFormations = formations.filter(f => !f.is_archived);
  const stats = {
    total: activeFormations.length,
    valides: activeFormations.filter(f => getStatut(f) === 'valide').length,
    expireSoon: activeFormations.filter(f => getStatut(f) === 'expire_soon').length,
    expires: activeFormations.filter(f => getStatut(f) === 'expire').length,
    archived: formations.filter(f => f.is_archived).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Formations</h1>
          <p className="text-gray-500">Gérez les formations et leurs types</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                <GraduationCap className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl md:text-2xl font-bold">{stats.total}</p>
                <p className="text-xs md:text-sm text-gray-500 truncate">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-2 bg-green-100 rounded-lg shrink-0">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl md:text-2xl font-bold">{stats.valides}</p>
                <p className="text-xs md:text-sm text-gray-500 truncate">Valides</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg shrink-0">
                <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-yellow-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl md:text-2xl font-bold">{stats.expireSoon}</p>
                <p className="text-xs md:text-sm text-gray-500 truncate">Expirent bientôt</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-2 bg-gray-100 rounded-lg shrink-0">
                {showArchives ? <Archive className="h-4 w-4 md:h-5 md:w-5 text-gray-600" /> : <Clock className="h-4 w-4 md:h-5 md:w-5 text-red-600" />}
              </div>
              <div className="min-w-0">
                <p className="text-xl md:text-2xl font-bold">{showArchives ? stats.archived : stats.expires}</p>
                <p className="text-xs md:text-sm text-gray-500 truncate">{showArchives ? 'Archivées' : 'Expirées'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="formations" className="gap-2">
              <List className="h-4 w-4" />
              Formations
            </TabsTrigger>
            <TabsTrigger value="types" className="gap-2">
              <Settings className="h-4 w-4" />
              Types de formations
            </TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-4">
            {activeTab === 'formations' && (
              <div className="flex items-center gap-2">
                <Switch
                  id="archives-formations"
                  checked={showArchives}
                  onCheckedChange={setShowArchives}
                />
                <Label htmlFor="archives-formations" className="text-sm flex items-center gap-1 cursor-pointer">
                  <Archive className="h-4 w-4" />
                  Voir les archives
                </Label>
              </div>
            )}
            {activeTab === 'formations' ? (
              <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="gap-2">
                <Plus className="h-4 w-4" />
                Ajouter une formation
              </Button>
            ) : (
              <Button onClick={() => { resetTypeForm(); setTypeDialogOpen(true); }} className="gap-2">
                <Plus className="h-4 w-4" />
                Nouveau type
              </Button>
            )}
          </div>
        </div>

        {/* Formations Tab */}
        <TabsContent value="formations">
          <Card>
            {/* Filters */}
            <CardHeader className="pb-4">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {typesFormations.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.intitule}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statutFilter} onValueChange={setStatutFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="valide">Valides</SelectItem>
                    <SelectItem value="expire_soon">Expire bientôt</SelectItem>
                    <SelectItem value="expire">Expirées</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredFormations.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Aucune formation trouvée</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Collaborateur</TableHead>
                      <TableHead className="whitespace-nowrap">Formation</TableHead>
                      <TableHead className="whitespace-nowrap">Date</TableHead>
                      <TableHead className="whitespace-nowrap">Validité</TableHead>
                      <TableHead className="whitespace-nowrap">Statut</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFormations.map(formation => {
                      const statut = getStatut(formation);
                      return (
                        <TableRow key={formation.id} className={formation.is_archived ? 'bg-gray-50 opacity-70' : ''}>
                          <TableCell className="font-medium whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {formation.collaborateur_nom || '-'}
                              {formation.is_archived && (
                                <Badge variant="secondary" className="text-xs">
                                  <Archive className="h-3 w-3 mr-1" />
                                  Archivée
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{formation.type_formation_code}</Badge>
                              {formation.intitule}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{formation.date_formation}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {formation.validite_type === 'vie' 
                              ? <Badge className="bg-purple-100 text-purple-800">À vie</Badge>
                              : formation.date_fin || '-'
                            }
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {statut === 'valide' && (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />Valide
                              </Badge>
                            )}
                            {statut === 'expire_soon' && (
                              <Badge className="bg-yellow-100 text-yellow-800">
                                <AlertTriangle className="h-3 w-3 mr-1" />Expire bientôt
                              </Badge>
                            )}
                            {statut === 'expire' && (
                              <Badge className="bg-red-100 text-red-800">
                                <Clock className="h-3 w-3 mr-1" />Expirée
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                title="Télécharger l'attestation"
                                onClick={() => handleDownloadAttestation(formation)}
                              >
                                <FileDown className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => {
                                  setSelectedFormation(formation);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Types Tab */}
        <TabsContent value="types">
          <Card>
            <CardContent className="p-0">
              {typesFormations.length === 0 ? (
                <div className="text-center py-12">
                  <Settings className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Aucun type de formation défini</p>
                  <Button variant="link" onClick={() => { resetTypeForm(); setTypeDialogOpen(true); }}>
                    Créer un type
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Code</TableHead>
                      <TableHead className="whitespace-nowrap">Intitulé</TableHead>
                      <TableHead className="whitespace-nowrap">Description</TableHead>
                      <TableHead className="whitespace-nowrap">Validité</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {typesFormations.map(type => (
                      <TableRow key={type.id}>
                        <TableCell className="whitespace-nowrap">
                          <Badge variant="outline" className="font-mono">{type.code}</Badge>
                        </TableCell>
                        <TableCell className="font-medium whitespace-nowrap">{type.intitule}</TableCell>
                        <TableCell className="text-gray-500 max-w-xs truncate">
                          {type.description || '-'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {type.validite_type === 'vie' ? (
                            <Badge className="bg-purple-100 text-purple-800">À vie</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800">
                              {type.validite_mois} mois
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditType(type)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                setSelectedType(type);
                                setDeleteTypeDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Formation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedFormation ? 'Modifier la formation' : 'Ajouter une formation'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Collaborateur *</Label>
                <Select 
                  value={formData.collaborateur_id} 
                  onValueChange={(v) => setFormData({ ...formData, collaborateur_id: v })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {collaborateurs.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.prenom} {c.nom} ({c.matricule})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Type de formation *</Label>
                <Select 
                  value={formData.type_formation_id} 
                  onValueChange={handleTypeChange}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typesFormations.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{type.code}</Badge>
                          {type.intitule}
                          <span className="text-gray-400 text-xs">
                            ({type.validite_type === 'vie' ? 'À vie' : `${type.validite_mois} mois`})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedTypeFormation && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">{selectedTypeFormation.intitule}</p>
                      <p>Validité : {selectedTypeFormation.validite_type === 'vie' ? 'À vie' : `${selectedTypeFormation.validite_mois} mois`}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Date de formation *</Label>
                <Input 
                  type="date" 
                  value={formData.date_formation} 
                  onChange={(e) => handleDateFormationChange(e.target.value)} 
                  required 
                />
              </div>

              {selectedTypeFormation?.validite_type !== 'vie' && (
                <div className="space-y-2">
                  <Label>Date d&apos;expiration</Label>
                  <Input 
                    type="date" 
                    value={formData.date_fin} 
                    onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })} 
                  />
                  <p className="text-xs text-gray-500">Calculée automatiquement selon le type</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Organisme</Label>
                  <Input 
                    value={formData.organisme} 
                    onChange={(e) => setFormData({ ...formData, organisme: e.target.value })} 
                    placeholder="AFPA, AFTRAL..." 
                  />
                </div>
                <div className="space-y-2">
                  <Label>N° Attestation</Label>
                  <Input 
                    value={formData.numero_attestation} 
                    onChange={(e) => setFormData({ ...formData, numero_attestation: e.target.value })} 
                    placeholder="ATT-2024-001" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="document">Justificatif de formation (optionnel)</Label>
                <Input
                  id="document"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setFormData({ ...formData, document: e.target.files[0] })}
                  className="cursor-pointer"
                />
                <p className="text-xs text-gray-500">Format accepté: PDF, JPG, PNG</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedFormation ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Type Formation Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedType ? 'Modifier le type de formation' : 'Nouveau type de formation'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTypeSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Code *</Label>
                  <Input 
                    value={typeFormData.code} 
                    onChange={(e) => setTypeFormData({ ...typeFormData, code: e.target.value.toUpperCase() })} 
                    placeholder="SEC-INC"
                    required
                    disabled={!!selectedType}
                  />
                  <p className="text-xs text-gray-500">Identifiant unique (ex: SST, HAB-ELEC)</p>
                </div>
                <div className="space-y-2">
                  <Label>Type de validité *</Label>
                  <Select 
                    value={typeFormData.validite_type} 
                    onValueChange={(v) => setTypeFormData({ ...typeFormData, validite_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="duree">Durée limitée</SelectItem>
                      <SelectItem value="vie">À vie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Intitulé *</Label>
                <Input 
                  value={typeFormData.intitule} 
                  onChange={(e) => setTypeFormData({ ...typeFormData, intitule: e.target.value })} 
                  placeholder="Sécurité incendie"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input 
                  value={typeFormData.description} 
                  onChange={(e) => setTypeFormData({ ...typeFormData, description: e.target.value })} 
                  placeholder="Formation obligatoire pour..."
                />
              </div>

              {typeFormData.validite_type === 'duree' && (
                <div className="space-y-2">
                  <Label>Durée de validité (mois) *</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={typeFormData.validite_mois} 
                    onChange={(e) => setTypeFormData({ ...typeFormData, validite_mois: parseInt(e.target.value) })} 
                    required
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTypeDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedType ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Formation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette formation ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Type Dialog */}
      <AlertDialog open={deleteTypeDialogOpen} onOpenChange={setDeleteTypeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le type <strong>{selectedType?.intitule}</strong> ?
              Les formations existantes de ce type ne seront pas supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteType} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
